import { IdGenerator } from "../services/models/helpers/id-generator";
import { TraceLoggingHelpers } from "../services/models/helpers/trace-logging-helpers";
import { ConnectionCallbacks, DesiredState, LifecycleManager, Outbox } from "./lifecycle-manager";
import { LoginManager } from "./login-manager";
import { SendManager } from "./send-manager";

declare const window: any;

export type subscribeOnConnectedCancelToken = () => void;
export type subscribeOnConnectedListener = () => void;

export type subscribeOnDisconnectedCancelToken = () => void;
export type subscribeOnDisconnectedListener = () => void;

export type subscribeOnReconnectedCancelToken = () => void;
export type subscribeOnReconnectedListener = () => void;

export type subscribeOnDisconnectedForGoodCancelToken = () => void;
export type subscribeOnDisconnectedForGoodListener = () => void;

export type subscribeOnPingHealthCancelToken = () => void;
export type subscribeOnPingHealthListener = (pingMs: number) => void;

export type subscribeConnectionStatusUpdateCancelToken = () => void;
export type subscribeConnectionStatusUpdateListener = (connected: boolean) => void;


/**
 * This is how the main program communicates with the server.
 */
export class ConnectionMaster {
  private onConnectedListenerDict = new Map<string, subscribeOnConnectedListener>();
  private onDisconnectedListenerDict = new Map<string, subscribeOnDisconnectedListener>();
  private onReconnectedListenerDict = new Map<string, subscribeOnReconnectedListener>();
  private onDisconnectedForGoodListenerDict = new Map<string, subscribeOnDisconnectedForGoodListener>();
  private onPingHealthListenerDict = new Map<string, subscribeOnPingHealthListener>();
  private connectionStatusUpdateListenerDict = new Map<string, subscribeConnectionStatusUpdateListener>();

  private onRestart(outbox: Outbox): void {
    this.#loginManager.connected(outbox);
    this.#sendManager.connected();
  }

  private onConnectionCallbacks: ConnectionCallbacks = {
    onConnected: () => {
      TraceLoggingHelpers.log('onConnectionCallbacks: onConnected');
      this.onConnected();
      this.onConnectionStatusUpdate(true);
    },
    onDisconnected: () => {
      TraceLoggingHelpers.log('onConnectionCallbacks: onDisconnected');
      this.onDisconnected();
      this.onConnectionStatusUpdate(false);
    },
    onReconnected: () => {
      TraceLoggingHelpers.log('onConnectionCallbacks: onReconnected');
      TraceLoggingHelpers.log('onConnectionCallbacks Current Message Listners: ', this.#lifecycleManager.currentMessageListners());
      this.onReconnected();
      this.onConnectionStatusUpdate(true);
    },
    onPingHealth: (pingMS: number) => {
      TraceLoggingHelpers.log('onConnectionCallbacks: onPingHealth', pingMS);
      TraceLoggingHelpers.log('onConnectionCallbacks Current Message Listners: ', this.#lifecycleManager.currentMessageListners());
      this.onPingHealth(pingMS);
      this.onConnectionStatusUpdate(true);
    },
    onDisconnectedForGood: () => {
      TraceLoggingHelpers.log('onConnectionCallbacks: onDisconnectedForGood');
      this.onDisconnectedForGood();
      this.onConnectionStatusUpdate(false);
    }
  }
  
  private onConnectionStatusUpdate(connected: boolean): void {
    this.connectionStatusUpdateListenerDict.forEach((value: subscribeConnectionStatusUpdateListener) => {
      value(connected);
    });
  }

  private onDisconnectedForGood(): void {
    this.onDisconnectedForGoodListenerDict.forEach((value: subscribeOnDisconnectedForGoodListener) => {
      value();
    });
  }  
  
  private onPingHealth(pingMs: number): void {
    this.onPingHealthListenerDict.forEach((value: subscribeOnPingHealthListener) => {
      value(pingMs);
    });
  }  

  private onConnected(): void {
    this.onConnectedListenerDict.forEach((value: subscribeOnConnectedListener) => {
      value();
    });
  }  

  private onDisconnected(): void {
    this.onDisconnectedListenerDict.forEach((value: subscribeOnDisconnectedListener) => {
      value();
    });
  }    

  private onReconnected(): void {
    this.onReconnectedListenerDict.forEach((value: subscribeOnReconnectedListener) => {
      value();
    });
  }   

  #lifecycleManager = new LifecycleManager(this.onRestart.bind(this), this.onConnectionCallbacks);
  #loginManager = new LoginManager(this.#lifecycleManager);
  #sendManager = new SendManager(this.#lifecycleManager);
  get lifecycleManager(): Pick<LifecycleManager, "softReset" | "connected"> {
    return this.#lifecycleManager;
  }
  get outbox(): Outbox {
    return this.#sendManager;
  }
  get login(): Pick<LoginManager, "login" | "logout" | "isLoggedIn" | "getCurrentUser"> {
    return this.#loginManager;
  }

  static #primary: ConnectionMaster | undefined;

  //static idStatusUpdateCounter = 0n;
  //public id = Math.floor(Math.random() * 1000000);

  public id = IdGenerator.NewId();

  /**
   * This will automatically create a connection on the first call, and reuse it for later calls.
   *
   * Can create more than one ConnectionMaster, but usually you won't.
   */
  static get primary(): ConnectionMaster {
    let isWebPage = typeof window !== 'undefined';
    if (isWebPage) {
      if (typeof window.primary !== 'undefined') {
        return window.primary;
      }

      if (!this.#primary) {
        this.#primary = new this();
      }

      window.primary = this.#primary;
      return window.primary;
    } else {
      if (!this.#primary) {
        this.#primary = new this();
      }

      return this.#primary;
    }
  }

  public reconnect() {
    this.#lifecycleManager.desiredState = DesiredState.DISCONNECTED;
    this.#lifecycleManager.desiredState = DesiredState.CONNECTED;
  }

  public subscribeConnectionStatusUpdate(listener: subscribeConnectionStatusUpdateListener): subscribeConnectionStatusUpdateCancelToken {
    const cancelId = IdGenerator.NewId();

    const cancelToken: subscribeConnectionStatusUpdateCancelToken = () => {
      this.connectionStatusUpdateListenerDict.delete(cancelId);
    };

    this.connectionStatusUpdateListenerDict.set(cancelId, listener);
    return cancelToken;
  }

  public subscribeOnConnected(listener: subscribeOnConnectedListener): subscribeOnConnectedCancelToken {
    const cancelId = IdGenerator.NewId();
    
    const cancelToken: subscribeOnConnectedCancelToken = () => {
      this.onConnectedListenerDict.delete(cancelId);
    };

    this.onConnectedListenerDict.set(cancelId, listener);

    return cancelToken;
  }  
  
  public subscribeOnDisconnected(listener: subscribeOnDisconnectedListener): subscribeOnDisconnectedCancelToken {
    const cancelId = IdGenerator.NewId();
    
    const cancelToken: subscribeOnDisconnectedCancelToken = () => {
      this.onDisconnectedListenerDict.delete(cancelId);
    };

    this.onDisconnectedListenerDict.set(cancelId, listener);

    return cancelToken;
  }    

  public subscribeOnReconnected(listener: subscribeOnReconnectedListener): subscribeOnReconnectedCancelToken {
    const cancelId = IdGenerator.NewId();
    
    const cancelToken: subscribeOnReconnectedCancelToken = () => {
      this.onReconnectedListenerDict.delete(cancelId);
    };

    this.onReconnectedListenerDict.set(cancelId, listener);

    return cancelToken;
  }  


  public subscribeOnDisconnectedForGood(listener: subscribeOnDisconnectedForGoodListener): subscribeOnDisconnectedForGoodCancelToken {
    const cancelId = IdGenerator.NewId();
    
    const cancelToken: subscribeOnDisconnectedForGoodCancelToken = () => {
      this.onDisconnectedForGoodListenerDict.delete(cancelId);
    };

    this.onDisconnectedForGoodListenerDict.set(cancelId, listener);

    return cancelToken;
  }

  public subscribeOnPingHealth(listener: subscribeOnPingHealthListener): subscribeOnPingHealthCancelToken {
    const cancelId = IdGenerator.NewId();
    
    const cancelToken: subscribeOnPingHealthCancelToken = () => {
      this.onPingHealthListenerDict.delete(cancelId);
    };

    this.onPingHealthListenerDict.set(cancelId, listener);

    return cancelToken;
  }  
}
